import React, { useEffect, useMemo, useState } from 'react'
import { Button, CircularProgress, Dialog, DialogTitle, Grid, Typography } from '@mui/material'
import axios from 'axios'
import ReactGA from 'react-ga4'
import { useNavigate } from 'react-router-dom'
import { AppConstants, LeadStage } from 'AppConstants/AppConstants'
import Footer from 'components/Footer'
import Header from 'components/Header'
import { InfoIconBlack, InfoIconWhite } from 'components/shared/infoIcon'
import useQueryParams from 'hooks/useQueryParams'
import { IPlan } from 'redux/booking'
import { SetAvailablePlansSignUp, SetSelectedPlanSignUp, SetSelectedSlotSignUp } from 'redux/SignUp/persist'
import { useAppDispatch, useAppSelector } from 'redux/store'
import { StatusEnum } from 'redux/types'
import { createNavigateWithQueryParams } from 'utils/navigateWithQueryParams'
import styles from './Plan.module.scss'

const styless: any = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 300,
  height: 170,
  overflow: 'hidden',
  backgroundColor: '#050624',
  color: 'white',
  borderRadius: '8px',
  p: 4,
}

const PlanSignUp = () => {
  const { getAllParams, getParams } = useQueryParams()
  const utm_source = getParams('utm_source')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Signup flow state management
  const plans = useAppSelector((state) => state.signUpPersist.availablePlansSignUp)
  const selectedPlanInfo = useAppSelector((state) => state.signUpPersist.selectedPlanSignUp)
  const isMoreThanOneStudio = useAppSelector((state) => state.signUpPersist.isMoreThanOneStudioSignUp)
  const selectedStudio = useAppSelector((state) => state.signUpPersist.selectedStudioSignUp)

  // Create reusable navigation function
  const navigateWithQueryParams = createNavigateWithQueryParams(navigate, getAllParams)

  useEffect(() => {
    if (!selectedStudio) {
      // No studio selected (or session state was lost): restart at studio selection, then slot, then plan.
      dispatch(SetSelectedSlotSignUp(null))
      navigateWithQueryParams('/signup/location')
    }
  }, [])
  const [error, setError] = useState(false)
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [disableButton, setDisableButton] = useState(true)
  const [loadingButton, setLoadingButton] = useState(false)
  const [fetchPlanStatus, setFetchPlanStatus] = useState<StatusEnum>(StatusEnum.Idle)
  const [userInfo, setUserInfo]: any = useState(null)
  const trackerCount = useAppSelector((state) => state.signUpPersist.trackerCountSignUp)
  const locParam = getParams('loc')
  // plans order
  const planList = useMemo(() => {
    if (plans) {
      const membershipPlan = plans?.find((plan) => plan.type === 'subscription')
      const otherPlans = plans?.filter((plan) => plan.type !== 'subscription')
      return membershipPlan ? [membershipPlan, ...otherPlans] : otherPlans
    }
  }, [plans])

  //choose plan

  const onContinue = async () => {
    navigateWithQueryParams('/signup/confirmation')
  }

  async function getPlans() {
    setFetchPlanStatus(StatusEnum.Pending)
    try {
      const response: any = await axios.get(`${AppConstants.API_URL}/payment-plan/v1`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('userToken')}`,
          'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
        },
      })
      if (response && response?.data && response?.data?.data) {
        dispatch(SetAvailablePlansSignUp(response?.data?.data))

        setFetchPlanStatus(StatusEnum.Success)
      } else if (response?.data?.statusCode === 401) {
        setFetchPlanStatus(StatusEnum.Failed)
        console.log('error')
      }
    } catch (error) {
      setFetchPlanStatus(StatusEnum.Failed)
      console.error(error)
    }
  }

  const postLeadStage = async () => {
    await axios
      .post(
        AppConstants.API_URL + '/lead/stage/update',
        { stage: LeadStage.plan },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
            'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
          },
        },
      )
      .then((res) => {
        return res
      })
      .catch((err) => {
        return err?.response
      })
  }

  useEffect(() => {
    postLeadStage()
  }, [])

  useEffect(() => {
    getPlans()
    // getProfile()
    if (selectedPlanInfo) {
      setDisableButton(false)
    }
  }, [])

  useEffect(() => {
    ReactGA.event('page_view', {
      page_title: 'SignUp|Plan',
      page_location: '/signup/plan',
    })
  }, [])

  return (
    <>
      <div className={styles.container}>
        <Header
          headerText=""
          showBackButton={true}
          onBack={() => {
            navigateWithQueryParams('/signup/slot')
          }}
        />
        <div className={styles.planDetails}>
          <div className={styles.choosePlanHeader}>Choose a plan.</div>
          {fetchPlanStatus === StatusEnum.Pending && (
            <div className={styles.loadingWrapper}>
              <CircularProgress style={{ color: '#050624' }} size={30}></CircularProgress>
            </div>
          )}
          {fetchPlanStatus === StatusEnum.Success && (
            <div>
              <form>
                <Grid container spacing={2}>
                  {planList?.map((plan: IPlan, index: number) => {
                    let membershipDescription: any = null
                    let onDemandDescription: any = null
                    if (plan.type === 'subscription') {
                      let updatedDescriptions: any = [
                        `Doctor-Guided Adjustments`,
                        'Monthly Nervous System Scan',
                        'Renews Automatically',
                        'Cancel Anytime',
                      ]
                      membershipDescription = updatedDescriptions
                    }
                    if (plan.type === 'on-demand') {
                      let updatedDescriptions: any = [`Consultation, Exam, and Adjustment`, 'One-Time Use per Person']
                      onDemandDescription = updatedDescriptions
                    }
                    return (
                      <Grid item xs={12} key={index}>
                        <div
                          onClick={(e) => {
                            dispatch(SetSelectedPlanSignUp(plan))
                            setDisableButton(false)
                          }}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '10px 15px 12px 15px',
                            boxShadow: '0 2px 2px rgba(0, 0, 0, 0.20)',
                            border:
                              selectedPlanInfo?.id === plan.id
                                ? '1px solid #007F7C'
                                : '1px solid var(--color-border-primary)',
                            borderRadius: '6px',
                            backgroundColor: selectedPlanInfo?.id === plan.id ? '#007F7C' : 'var(--color-background)',
                            color:
                              selectedPlanInfo?.id === plan.id
                                ? 'var(--color-text-secondary)'
                                : 'var(--color-text-primary)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <div style={{ textTransform: 'capitalize' }}>
                              {plan?.type === 'subscription' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <img
                                    src={'/images/logoV3.svg'}
                                    style={{
                                      width: '22px',
                                      height: '22px',
                                      border: selectedPlanInfo?.id === plan.id ? '1.5px solid #E5E7EB' : 'none',
                                      borderRadius: '5px',
                                    }}
                                    alt="logo"
                                  ></img>
                                  <span style={{ fontWeight: 700 }}>Membership</span>
                                </div>
                              )}
                              {plan?.type === 'on-demand' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontWeight: 700 }}>New Patient Special</span>
                                </div>
                              )}
                            </div>
                            <span style={{ fontWeight: 600 }}>
                              $ {plan.type === 'subscription' ? `${plan.amount / 100}/mo.` : plan.amount / 100}
                            </span>
                          </div>
                          {membershipDescription !== null && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                              }}
                            >
                              {membershipDescription.map((list: any, index: number) => {
                                return (
                                  <div
                                    key={index}
                                    style={{
                                      display: 'flex',
                                      gap: '16px',
                                      alignItems: 'center',
                                      marginTop: '8px',
                                      marginLeft: '7px',
                                    }}
                                  >
                                    <img
                                      src={
                                        selectedPlanInfo?.id === plan.id
                                          ? '/images/bulletsWhite.svg'
                                          : '/images/bulletsBlack.svg'
                                      }
                                      style={{ width: '8px', height: '8px' }}
                                      alt="tick"
                                    ></img>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontSize: '14px',
                                        fontWeight: 100,
                                      }}
                                    >
                                      {list}
                                      {index === 0 && (
                                        <div
                                          style={{
                                            height: '18px',
                                            width: '18px',
                                            marginTop: '5px',
                                            marginLeft: '6px',
                                            cursor: 'pointer',
                                          }}
                                          onClick={() => {
                                            setShowInfoModal(true)
                                          }}
                                        >
                                          {selectedPlanInfo?.id === plan.id ? <InfoIconWhite /> : <InfoIconBlack />}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                              {/* <p>{plan?.description}</p> */}
                            </div>
                          )}
                          {onDemandDescription !== null && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                              }}
                            >
                              {onDemandDescription.map((list: any, index: number) => {
                                return (
                                  <div
                                    key={index}
                                    style={{
                                      display: 'flex',
                                      gap: '16px',
                                      alignItems: 'center',
                                      marginTop: '8px',
                                      marginLeft: '7px',
                                    }}
                                  >
                                    <img
                                      src={
                                        selectedPlanInfo?.id === plan.id
                                          ? '/images/bulletsWhite.svg'
                                          : '/images/bulletsBlack.svg'
                                      }
                                      style={{ width: '8px', height: '8px' }}
                                      alt="tick"
                                    ></img>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontSize: '14px',
                                        fontWeight: 100,
                                      }}
                                    >
                                      {list}
                                    </div>
                                  </div>
                                )
                              })}
                              {/* <p>{plan?.description}</p> */}
                            </div>
                          )}
                        </div>
                      </Grid>
                    )
                  })}
                </Grid>
              </form>
              {/* <div
                style={{
                  color: '#E5E7EB',
                  fontSize: '16px',
                  marginTop: '30px',
                  marginLeft: '1px',
                }}
              >
                <b>All Plans Include a Complimentary Screening</b>
                <br />
                <div style={{ marginTop: '1rem', lineHeight: '1.5rem' }}>
                  Following your screening, if you choose to opt-out, we'll refund you 100%.
                </div>
              </div> */}
            </div>
          )}
        </div>

        <div className={styles.btnWrapper}>
          <Grid item xs={12}>
            {showInfoModal && (
              <Dialog
                onClose={() => {}}
                open={showInfoModal}
                PaperProps={{
                  style: {
                    background: 'var(--color-background)',
                    borderRadius: '12px',
                    padding: '10px 12px',
                    minWidth: 320,
                    maxWidth: 300,
                    minHeight: 'auto',
                    // height: 170,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    flex: 1, // align everything to the left
                    textAlign: 'left', // global text alignment
                  },
                }}
              >
                <DialogTitle
                  style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: 'var(--color-text-primary)',
                    textAlign: 'left',
                    marginBottom: '12px',
                    padding: 0,
                    width: '100%',
                  }}
                >
                  Membership Details
                </DialogTitle>
                <Typography
                  style={{
                    color: 'var(--color-text-primary)',
                    fontSize: '14px',
                    textAlign: 'left',
                    marginBottom: '24px',
                    lineHeight: 1.5,
                    width: '100%',
                  }}
                >
                  Membership covers all visits recommended by your doctor for one monthly price. Care frequency is
                  determined by your doctor. This membership is not health insurance and does not replace coverage.
                </Typography>
                <Button
                  color="primary"
                  style={{
                    marginBottom: '0.3rem',
                    height: '42px',
                    padding: '0px 8px 0px 8px',
                    fontSize: '14px',
                    alignSelf: 'center',
                  }}
                  onClick={() => {
                    setShowInfoModal(false)
                  }}
                >
                  Ok
                </Button>
              </Dialog>
            )}
            {/* {error && (
              <div className={styles.errorContainer}>
                <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>Please choose a plan you want ?</Typography>
                <div
                  style={{
                    display: 'flex',
                    flex: '1',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    paddingRight: '12px',
                  }}
                >
                  <img
                    onClick={() => {
                      setError(false)
                    }}
                    style={{ cursor: 'pointer' }}
                    src={'/images/wrong.svg'}
                  ></img>
                </div>
              </div>
            )} */}
          </Grid>
        </div>
        <Footer
          buttonText={'Continue'}
          isLoading={loadingButton}
          trackerIndex={6}
          shouldShowFour={true}
          shouldShowFive={true}
          shouldShowSix={true}
          shouldShowSeven={trackerCount >= 7}
          shouldShowEight={trackerCount >= 8}
          isDisabled={disableButton}
          buttonType={'submit'}
          onButtonClick={() => {
            disableButton ? undefined : onContinue()
          }}
        />
      </div>
    </>
  )
}

export default PlanSignUp
